import { ConfirmPassword } from '@ui/form-items';
import { coreActions } from '@sonar-software/sonar-core';
import { createAsyncSelectors } from '@lib/selectors';
import { CurrentPasswordModal } from '@ui/CurrentPasswordModal';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { SonarAlert } from '@ui/SonarAlert';
import { useSelector } from 'react-redux';

import { AUTOCOMPLETE, COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { Button, Form, Row } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useAction, useAppNotifications, useAsyncState, useBoolean, useOnUnmount } from '@hooks';

const selectors = createAsyncSelectors((state) => state.core.users.byId.password.patch);

export const ChangePassword: React.FC = () => {
	const [ displayModal, setDisplayModal ] = useBoolean(false);
	const [ form ] = Form.useForm();
	const { successNotification } = useAppNotifications();

	const { isErrored, isFailed, isRequested, isReceived } = useAsyncState(selectors);
	const statusCode = useSelector(selectors.statusCode);
	const requestPasswordChange = useAction(coreActions.requestPatchCoreUsersByIdPassword);
	const resetRequest = useAction(coreActions.resetPatchCoreUsersByIdPassword);

	const handleOk = useCallback((currentPassword: string) => {
		const desiredPassword = form.getFieldValue('confirmPassword');

		requestPasswordChange({
			currentPassword,
			desiredPassword
		});
	}, [ form, requestPasswordChange ]);

	const handleCancel = useCallback(() => {
		setDisplayModal.off();
		resetRequest();
	}, [ resetRequest, setDisplayModal ]);

	const handleAlertClose = useCallback(() => {
		resetRequest();
	}, [ resetRequest ]);

	useEffect(() => {
		if (isReceived) {
			resetRequest();
			setDisplayModal.off();
			form.resetFields();

			successNotification(NOTIFICATIONS_COPY.SUCCESS_PATCH_USER_CHANGE_PASS);
		}
	}, [ form, isReceived, resetRequest, setDisplayModal, successNotification ]);

	useEffect(() => {
		if (statusCode === ErrorOrFailStatusCode.UnprocessableEntity) {
			setDisplayModal.off();
		}
	}, [ setDisplayModal, statusCode ]);

	// Reset request state on unmount to clear error alerts when navigating away
	useOnUnmount(() => {
		resetRequest();
	});

	return (
		<div id='change-password'>
			<Form
				form={ form }
				name='changePassword'
				onFinish={ setDisplayModal.on }>
				<ConfirmPassword
					autoComplete={ AUTOCOMPLETE.NEW_PASSWORD }
					disabled={ isRequested }
					errorAlert={
						<SonarAlert
							active={ statusCode === ErrorOrFailStatusCode.UnprocessableEntity }
							afterClose={ handleAlertClose }
							closable
							message={ NOTIFICATIONS_COPY.ERROR_PATCH_USER_CHANGE_PASS_REUSED }
							style={ { marginBottom: 16 } }
							type='error' />
					}
					id='change-password' />
				<CurrentPasswordModal
					error={ isErrored }
					errorMessage={ NOTIFICATIONS_COPY.ERROR_PATCH_USER_CHANGE_PASS }
					failed={ isFailed }
					failedMessage={ NOTIFICATIONS_COPY.FAILED_PATCH_USER_CHANGE_PASS }
					onCancel={ handleCancel }
					onOk={ handleOk }
					open={ displayModal } />
				<Row justify='space-between'>
					<Button
						disabled={ isRequested }
						htmlType='submit'
						id='change-password-submit-button'
						type='primary'>
						{ COPY_SPECIFIC.CHANGE_PASSWORD }
					</Button>
				</Row>
			</Form>
		</div>
	);
};
